import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const ResponsibleGambling = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <article className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link
              to="/guides"
              data-discover="true"
              className="inline-flex items-center space-x-2 text-yellow-accent hover:text-yellow-light mb-8 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Back to Guides</span>
            </Link>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-dark mb-6">
              Responsible Gambling: Play Smart, Stay Safe
            </h1>
            
            <div className="prose prose-lg max-w-none text-gray-light leading-relaxed space-y-6">
              <p className="text-xl text-gray-medium">
                Casino gaming should entertain, not harm. Responsible gambling means maintaining control, 
                setting limits, and recognizing when to stop. This guide provides essential tools and 
                strategies to help you enjoy casino gaming safely while protecting yourself from potential 
                problems.
              </p>

              <h2 className="text-3xl font-bold text-gray-dark mt-12 mb-6">Setting Budget Limits</h2>
              
              <p>
                Before placing your first bet, decide how much you can afford to lose. This amount should 
                come from your entertainment budget, never from essential expenses like rent, bills, or 
                savings. Treat casino gaming like any other entertainment expense – budgeted, planned, 
                and limited.
              </p>
              
              <p>
                Set three types of limits: session limits (how much per playing session), daily limits 
                (how much per day), and monthly limits (how much per month). Stick to these limits 
                religiously – they're meaningless if you exceed them when losing. Use casino deposit 
                limit tools to enforce these boundaries automatically.
              </p>
              
              <p>
                Never chase losses by increasing bets or depositing more than planned. Losses are part 
                of casino gaming – accept them as the cost of entertainment. Chasing losses leads to 
                bigger losses and creates financial problems. If you've hit your limit, stop playing 
                and return another day.
              </p>

              <h2 className="text-3xl font-bold text-gray-dark mt-12 mb-6">Time Management</h2>
              
              <p>
                Set time limits for playing sessions, not just money limits. Casino games are designed 
                to keep you engaged, and time can pass quickly when you're absorbed in gameplay. Use 
                alarms or timers to remind yourself when sessions should end.
              </p>
              
              <p>
                Take regular breaks during extended play sessions. Step away from the screen, do 
                something else, and return with fresh perspective. Continuous play leads to fatigue, 
                which impairs decision-making and increases risk-taking behavior.
              </p>
              
              <p>
                Avoid playing when emotional – whether stressed, angry, sad, or overly excited. 
                Emotional states cloud judgment and lead to poor decisions. Casino gaming requires 
                clear thinking and rational choices, which are impossible when emotions run high.
              </p>

              <h2 className="text-3xl font-bold text-gray-dark mt-12 mb-6">Understanding the Odds</h2>
              
              <p>
                Every casino game favors the house – this is how casinos make money. Understanding 
                that you're statistically likely to lose over time helps maintain realistic expectations. 
                Wins are possible, but losses are more probable – accept this before playing.
              </p>
              
              <p>
                Don't believe in "due" wins or hot streaks. Each spin, hand, or roll is independent 
                – past results don't influence future outcomes. A slot that hasn't paid in 100 spins 
                isn't "due" to pay – it has the same probability on spin 101 as it did on spin 1.
              </p>
              
              <p>
                Learn about RTP (Return to Player) percentages and house edges. Slots typically have 
                94-97% RTP, meaning you'll lose 3-6% over time. Table games have varying house edges 
                – blackjack offers the best odds with proper strategy, while some bets on roulette 
                have higher edges. Understanding these numbers helps set realistic expectations.
              </p>

              <h2 className="text-3xl font-bold text-gray-dark mt-12 mb-6">Recognizing Problem Signs</h2>
              
              <p>
                Problem gambling develops gradually, making it easy to miss warning signs. Common 
                indicators include spending more than you can afford, chasing losses, lying about 
                gambling, neglecting responsibilities, or feeling anxious when not playing. If you 
                recognize these signs in yourself, take action immediately.
              </p>
              
              <p>
                Other warning signs include borrowing money to gamble, gambling to escape problems, 
                feeling unable to stop, or gambling until money runs out. These behaviors indicate 
                that gambling has moved beyond entertainment into problematic territory.
              </p>
              
              <p>
                Friends and family often notice problem gambling before you do. If loved ones express 
                concern about your gambling, listen seriously. They see changes you might miss, and 
                their perspective can help you recognize problems early.
              </p>

              <h2 className="text-3xl font-bold text-gray-dark mt-12 mb-6">Self-Exclusion Tools</h2>
              
              <p>
                All licensed UK casinos offer self-exclusion tools that let you block access to your 
                account temporarily or permanently. These tools are powerful and should be used if 
                you feel gambling is becoming problematic. Self-exclusion periods range from 24 hours 
                to permanent bans.
              </p>
              
              <p>
                GamStop provides industry-wide self-exclusion across all UK licensed operators. 
                Registering with GamStop blocks access to every licensed casino for your chosen 
                period – 6 months, 1 year, or 5 years. This prevents switching between casinos 
                to continue gambling.
              </p>
              
              <p>
                Self-exclusion isn't failure – it's responsible action. Using these tools demonstrates 
                self-awareness and commitment to maintaining control. If you're considering 
                self-exclusion, you probably need it – don't hesitate to use these tools.
              </p>

              <h2 className="text-3xl font-bold text-gray-dark mt-12 mb-6">Deposit and Loss Limits</h2>
              
              <p>
                Set deposit limits before you need them – when you're thinking clearly, not when 
                you're losing. Most casinos allow you to set daily, weekly, or monthly deposit 
                limits that can't be increased for 24 hours. This cooling-off period prevents 
                impulsive decisions during emotional moments.
              </p>
              
              <p>
                Loss limits work similarly, automatically stopping play when you've lost a specified 
                amount. These limits protect you from significant losses during single sessions. 
                Combine deposit and loss limits for comprehensive protection.
              </p>
              
              <p>
                Reality checks remind you how long you've been playing. Set these to appear every 
                30 minutes or hour, showing your session time and spending. These reminders help 
                maintain awareness and prevent extended play sessions.
              </p>

              <h2 className="text-3xl font-bold text-gray-dark mt-12 mb-6">Getting Help</h2>
              
              <p>
                If gambling feels out of control, professional help is available. BeGambleAware 
                provides free, confidential support and resources for problem gambling. Their website 
                offers self-assessment tools, advice, and links to treatment services.
              </p>
              
              <p>
                GamCare offers similar support with trained advisors available via phone, live chat, 
                or email. They provide non-judgmental support and can help you develop strategies 
                for regaining control. These services are free, confidential, and available 24/7.
              </p>
              
              <p>
                Support groups provide peer support from others who've experienced similar challenges. 
                Gamblers Anonymous offers meetings and online support for those seeking recovery. 
                Sharing experiences with others who understand can be powerful and healing.
              </p>

              <h2 className="text-3xl font-bold text-gray-dark mt-12 mb-6">Protecting Others</h2>
              
              <p>
                If you're concerned about someone else's gambling, approach conversations with 
                compassion and without judgment. Express concern about specific behaviors you've 
                noticed rather than making accusations. Offer support and information about help 
                resources without forcing action.
              </p>
              
              <p>
                You can't force someone to stop gambling, but you can encourage them to seek help. 
                Provide information about support services and be available to listen. However, 
                protect yourself financially – don't lend money or pay gambling debts, as this 
                enables continued problematic behavior.
              </p>

              <h2 className="text-3xl font-bold text-gray-dark mt-12 mb-6">Maintaining Balance</h2>
              
              <p>
                Healthy casino gaming fits into a balanced life alongside other activities and 
                responsibilities. If gambling dominates your time, thoughts, or finances, it's 
                become unbalanced. Maintain hobbies, relationships, and responsibilities outside 
                of gambling.
              </p>
              
              <p>
                View casino gaming as one form of entertainment among many, not your primary hobby 
                or source of excitement. Diversify your activities and interests to prevent gambling 
                from becoming central to your life.
              </p>

              <div className="bg-yellow-accent/10 border-l-4 border-yellow-accent p-6 my-8 rounded-r-lg">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-yellow-accent flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-bold text-gray-dark mb-2">Key Takeaway</h3>
                    <p className="text-gray-light">
                      Responsible gambling means maintaining control through limits, awareness, and 
                      balance. Set budgets and time limits before playing, recognize warning signs 
                      early, and use self-exclusion tools when needed. Casino gaming should enhance 
                      your life through entertainment, not create problems through excess. If gambling 
                      feels out of control, professional help is available – use it without shame 
                      or hesitation.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default ResponsibleGambling;

